import type { Post } from "@/sanity.types"
import CalendarL from "images/Icons/CalendarL.inline.svg"
import TagL from "images/Icons/TagL.inline.svg"
import UserL from "images/Icons/UserL.inline.svg"
import { DisplayDate } from "library/DisplayDate"
import SanityImage from "library/UniversalImage"
import UniversalLink from "library/link"
import { css, f, keyframes, styled } from "library/styled"
import colors from "styles/colors"
import textStyles, { clampText } from "styles/text"

export function LargeCard({ post }: { post: Post | null | undefined }) {
	if (!post) return null
	const {
		slug,
		author,
		publishOverride,
		mainImage,
		categories,
		title,
		preview,
		_createdAt,
	} = post

	const published = publishOverride ?? _createdAt

	const publishedDate = (
		<DisplayDate
			date={published}
			options={{
				year: "numeric",
				month: "long",
				day: "numeric",
			}}
		/>
	)

	const link = post?.externalUrl ?? `/blog/post/${slug?.current}`

	if (!link) return null

	return (
		<Wrapper href={link}>
			<PreventInitialTransition />
			<ImageWrapper>
				<BlogImage
					className="large-image"
					src={mainImage}
					width={875}
					height={526}
					loading="default"
				/>
			</ImageWrapper>
			<TitleContent>
				<UpperContent>
					<Title>{title}</Title>
					<Description>{preview}</Description>
				</UpperContent>
				<InfoColumn>
					<Author>
						<Icon>
							<UserL />
						</Icon>
						{author}
					</Author>
					<Line />
					<PublishDate>
						<Icon>
							<CalendarL />
						</Icon>
						{publishedDate ?? "Today"}
					</PublishDate>
					<Line />
					<Tag>
						<Icon>
							<TagL />
						</Icon>
						{categories?.[0]}
					</Tag>
				</InfoColumn>
			</TitleContent>
		</Wrapper>
	)
}

const InfoColumn = styled("div", {
	...f.responsive(css`
		display: flex;
		flex-direction: column;
		gap: 16px;
		${textStyles.titleS};
		color: ${colors.green400};
	`),
})

const Author = styled("div", {
	...f.responsive(css`
		display: flex;
		align-items: center;
		gap: 6px;
	`),
})

const PublishDate = styled("div", {
	...f.responsive(css`
		display: flex;
		align-items: center;
		gap: 6px;
	`),
})

const PreventInitialTransition = keyframes`
	0%,
	100% {
		transition: none;
	}
`

const BlogImage = styled(SanityImage, {
	...f.responsive(css`
		width: 100%;
		height: 100%;

		img: {
			transform-origin: center;
			transform: scale(1);
			transition: transform 0.2s ease-in-out;
			animation: PreventInitialTransition 0.1s;
		}
	`),
})

const Wrapper = styled(UniversalLink, {
	...f.responsive(css`
		display: flex;
		flex-shrink: 0;
		left: 0;
		z-index: 8;
		position: relative;
		border: 1px solid ${colors.green100};
		background-color: ${colors.offWhite};
		transition: 0.2s ease-in-out;
		width: 1376px;
		height: 526px;
		border-radius: 24px;
		overflow: clip;

		&:hover {
			background-color: ${colors.js.green100}50;

			img {
				transition: transform 0.2s ease-in-out;
				transform: scale(1.07);
			}
		}

		&:not(:hover) {
			img {
				transition: transform 0.2s ease-in-out;
				transform: scale(1);
			}
		}
	`),

	...f.tablet(css`
		width: 960px;
		height: 526px;
	`),

	...f.mobile(css`
		flex-direction: column;
		width: 327px;
		height: auto;
	`),
})

const TitleContent = styled("div", {
	...f.responsive(css`
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		position: relative;
		width: 485px;
		padding: 24px;
		gap: 30px;
	`),

	...f.responsive(css`
		width: 434px;
		padding: 24px;
	`),

	...f.mobile(css`
		width: 100%;
		padding: 16px;
	`),
})

const Title = styled("h2", {
	...f.responsive(css`
		${textStyles.h6};
		color: ${colors.green500};
		margin: -10px 0;
		padding: 10px 0;
		${clampText(5)};
	`),

	...f.tablet(css`
		margin-bottom: 16px;
	`),

	...f.mobile(css`
		${textStyles.bodyL};
		margin-bottom: 16px;
	`),
})

const Description = styled("p", {
	...f.responsive(css`
		${textStyles.bodyS};
		color: ${colors.green400};
		${clampText(3)};
		margin: -5px 0;
		padding: 35px 0 5px;
	`),

	...f.tablet(css`
		${textStyles.bodyS};
	`),

	...f.mobile(css`
		${textStyles.bodyS};
		margin-bottom: 32px;
	`),
})

const Tag = styled("div", {
	...f.responsive(css`
		${textStyles.bodyXXS};
		display: flex;
		align-items: center;
	`),
})

const Icon = styled("div", {
	...f.responsive(css`
		width: 24px;
		height: 24px;
		color: ${colors.green400};
		margin-right: 12px;
	`),

	svg: {
		width: "100%",
		height: "100%",
	},
})

const Line = styled("div", {
	...f.responsive(css`
		width: 100%;
		height: 1px;
		background-color: ${colors.green100};
	`),
})

const UpperContent = styled("div", {})

const ImageWrapper = styled("div", {
	...f.responsive(css`
		position: relative;
		width: 875px;
		height: 526px;
		border-radius: 24px 0 0 24px;
		overflow: clip;
	`),

	...f.tablet(css`
		width: 526px;
		height: 526px;
	`),

	...f.mobile(css`
		width: 343px;
		height: 345px;
		border-radius: 8px 8px 0 0;
	`),
})
